import 'nprogress/nprogress.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import 'react-perfect-scrollbar/dist/css/styles.css';
import './styles/global.css';
import './theme/mui-styles-augmentation';

import { Component, StrictMode } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router';
import { StyledEngineProvider } from '@mui/material/styles';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import App from './App';
import { AuthProvider } from './contexts/JWTContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { persistor, store } from './store/store';

/** After deploy / HMR, tabs may request stale Vite chunks — reload once to recover. */
const STALE_CHUNK_RELOAD_KEY = 'vite_stale_chunk_reloaded';

const isStaleDynamicImportError = (message: string | undefined): boolean => {
  const msg = (message ?? '').toLowerCase();
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('failed to fetch')
  );
};

const reloadForStaleChunk = (): boolean => {
  try {
    if (sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY)) return false;
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
    window.location.reload();
    return true;
  } catch {
    window.location.reload();
    return true;
  }
};

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadForStaleChunk();
});

class RootErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    if (isStaleDynamicImportError(error?.message) && reloadForStaleChunk()) {
      return { error: null };
    }
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info);
    if (isStaleDynamicImportError(error?.message)) {
      reloadForStaleChunk();
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, fontFamily: 'Inter, sans-serif' }}>
          <h1>Something went wrong</h1>
          <pre style={{ whiteSpace: 'pre-wrap', color: '#b91c1c' }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element #root not found');
}

createRoot(container).render(
  <StrictMode>
    <RootErrorBoundary>
      <Provider store={store}>
        <PersistGate
          loading={null}
          persistor={persistor}
        >
          <HelmetProvider>
            <StyledEngineProvider injectFirst>
              <SettingsProvider>
                <BrowserRouter>
                  <AuthProvider>
                    <I18nextProvider i18n={i18n}>
                      <App />
                    </I18nextProvider>
                  </AuthProvider>
                </BrowserRouter>
              </SettingsProvider>
            </StyledEngineProvider>
          </HelmetProvider>
        </PersistGate>
      </Provider>
    </RootErrorBoundary>
  </StrictMode>
);
